// Beat detector: instant-vs-history energy comparison with cooldown and floor.
// History is a ring buffer of length historyLen, initialized to zeros.
export class BeatDetector {
  constructor({ historyLen = 43, thresholdMult = 1.30, cooldownMs = 200, floor = 0.05 } = {}) {
    this.historyLen = historyLen;
    this.thresholdMult = thresholdMult;
    this.cooldownMs = cooldownMs;
    this.floor = floor;
    this.history = new Float32Array(historyLen);
    this.idx = 0;
    this.lastBeatTime = -Infinity;
  }

  detect(instEnergy, nowMs) {
    let sum = 0;
    for (let i = 0; i < this.historyLen; i++) sum += this.history[i];
    const mean = sum / this.historyLen;

    const fire = instEnergy > mean * this.thresholdMult
      && nowMs - this.lastBeatTime > this.cooldownMs
      && instEnergy > this.floor;

    // push AFTER threshold check
    this.history[this.idx] = instEnergy;
    this.idx = (this.idx + 1) % this.historyLen;

    if (fire) {
      this.lastBeatTime = nowMs;
      return true;
    }
    return false;
  }
}

// Onset detector: positive-rectified spectral flux above threshold, with cooldown.
// pulse is exposed for the engine to drive a spring envelope.
export class OnsetDetector {
  constructor({ threshold = 8.0, cooldownMs = 80, decay = 0.93 } = {}) {
    this.threshold = threshold;
    this.cooldownMs = cooldownMs;
    this.decay = decay;
    this.prev = null;
    this.lastOnsetTime = -Infinity;
    this.pulse = 0;
    // M6: continuous normalized flux 0..1 for the fractal shader. Running-max
    // peak with slow decay so the normalization adapts to source loudness.
    this.lastFlux = 0;
    this._fluxPeak = 1e-6;
  }

  detect(raw, nowMs) {
    const len = raw.length;
    if (!this.prev || this.prev.length !== len) {
      this.prev = new Uint8Array(len);
    }
    let flux = 0;
    for (let i = 4; i < len; i++) {
      const diff = raw[i] - this.prev[i];
      if (diff > 0) flux += diff;
    }
    flux /= 255;
    this.prev.set(raw);

    this._fluxPeak = Math.max(this._fluxPeak * 0.995, flux);
    this.lastFlux = Math.min(1.0, flux / Math.max(this._fluxPeak, 1e-6));

    const fire = flux > this.threshold && nowMs - this.lastOnsetTime > this.cooldownMs;
    if (fire) {
      this.lastOnsetTime = nowMs;
      this.pulse = 1.0;
      return true;
    }
    this.pulse *= this.decay;
    return false;
  }
}
